/**
 * TanStack Query key factory + retention helpers for dashboard tile results
 * (S3, G15 conventions). ONE shared query client — never create a second.
 *
 * Retention contract: the cache is dropped when the dashboard TAB closes
 * (call `evictAllDashboardTileQueries` from the tab unmount) and kept while
 * switching dashboards within the tab (no eviction on switch) — the playlist
 * (S13) and snapshot exporter (S15) rely on it.
 */
import type { QueryClient, QueryKey } from '@tanstack/react-query';

export const DASHBOARD_TILE_QUERY_DOMAIN = 'dashboard-tile';

/**
 * Stable, order-independent, LOSSLESS key for a small JSON-able value: the
 * canonicalised JSON string itself (object keys sorted at every level,
 * arrays kept in call order), not a digest of it.
 *
 * A digest MUST NOT be used here: folding the canonical string to a 32-bit
 * djb2 hash let two distinct param values collide onto the same TanStack
 * Query key — `{ p: 'AQ' }` and `{ p: 'B0' }` both folded to 2226
 * (`'A'*33+'Q' === 'B'*33+'0'`), so switching between them served the first
 * value's cached result instead of fetching the second. The canonical JSON
 * string is injective for the value shapes we key on, so distinct values can
 * never produce the same string.
 *
 * This value is used ONLY as a TanStack Query key segment (in-memory,
 * evicted with the dashboard tab) — never persisted, put in a file name/URL,
 * or sent over IPC/telemetry — so there is no size or opacity constraint
 * that would justify trading correctness for a shorter digest. TanStack
 * hashes the whole key itself with its own stable, key-sorted serializer, so
 * passing the full string costs nothing extra. A consumer that genuinely
 * needs a short/opaque id (a file name, a telemetry property) should derive
 * its own digest from this string rather than relying on this one being
 * short.
 */
export const stableValueKey = (value: unknown): string =>
  JSON.stringify(value, (_key, v: unknown) =>
    v !== null && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)))
      : v,
  );

export const dashboardTileQueryKey = (
  dashboardId: string,
  tileId: string,
  /** `tileRunInputsHash`: the tile's data inputs + its parameter fingerprint. */
  runInputsHash: string,
  filtersHash: string,
) => [DASHBOARD_TILE_QUERY_DOMAIN, dashboardId, tileId, runInputsHash, filtersHash] as const;

/**
 * Key for a tile under the DEFAULT (empty) params/filters — what
 * `useTileResult` uses until S10/S11 wire real values. Manual runs and
 * edit-invalidation address the cache through this.
 */
export const defaultTileQueryKey = (dashboardId: string, tileId: string) =>
  dashboardTileQueryKey(dashboardId, tileId, stableValueKey({}), stableValueKey([]));

// Retain one selected variant per tile across dashboard switches, including
// inactive cached tiles used by playlists/background alerts. Observer activity
// alone cannot identify which parameter/filter combination should refresh.
const selectedKeys = new WeakMap<QueryClient, Map<string, QueryKey>>();
const tileIdentity = (dashboardId: string, tileId: string) => JSON.stringify([dashboardId, tileId]);

export function selectDashboardTileQuery(client: QueryClient, key: ReturnType<typeof dashboardTileQueryKey>, enabled = true): void {
  const keys = selectedKeys.get(client) ?? new Map<string, QueryKey>();
  selectedKeys.set(client, keys);
  if (!enabled) {
    keys.delete(tileIdentity(key[1], key[2]));
    return;
  }
  keys.set(tileIdentity(key[1], key[2]), key);
  const encoded = JSON.stringify(key);
  // Removing obsolete variants also aborts their own signals; late completions
  // cannot refill the selected entry or cancel the new execution.
  client.removeQueries({
    queryKey: key.slice(0, 3),
    predicate: query => JSON.stringify(query.queryKey) !== encoded,
  });
}

export function isSelectedDashboardTileQuery(client: QueryClient, key: ReturnType<typeof dashboardTileQueryKey>): boolean {
  return JSON.stringify(selectedKeys.get(client)?.get(tileIdentity(key[1], key[2]))) === JSON.stringify(key);
}

export async function refetchCurrentDashboardTile(client: QueryClient, dashboardId: string, tileId: string): Promise<void> {
  const key = selectedKeys.get(client)?.get(tileIdentity(dashboardId, tileId));
  if (key) await client.refetchQueries({ queryKey: key, exact: true });
}

export async function refetchCurrentDashboardQueries(client: QueryClient, dashboardId: string): Promise<void> {
  const keys = [...(selectedKeys.get(client)?.values() ?? [])].filter(key => key[1] === dashboardId);
  await Promise.all(keys.map(queryKey => client.refetchQueries({ queryKey, exact: true })));
}

/** Drop every tile result — the dashboard tab closed. */
export const evictAllDashboardTileQueries = (queryClient: QueryClient): void => {
  selectedKeys.delete(queryClient);
  queryClient.removeQueries({ queryKey: [DASHBOARD_TILE_QUERY_DOMAIN] });
};
